"use client";

import { Check, Copy, Facebook, Linkedin, Share2, Twitter } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";

type ShareButtonProps = {
  title: string;
  description: string;
  url?: string;
  size?: "sm" | "lg" | "icon";
  variant?: "primary" | "outline" | "ghost" | "secondary";
  showLabel?: boolean;
  className?: string;
};

export function ShareButton({
  title,
  description,
  url,
  size = "sm",
  variant = "outline",
  showLabel = false,
  className,
}: ShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { copied, copy } = useCopyToClipboard({ timeout: 2000 });
  const shareUrl =
    url || (typeof window !== "undefined" ? window.location.href : "");

  // Native Web Share API
  async function handleNativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url: shareUrl,
        });
        setIsOpen(false);
      } catch (err) {
        // Silently ignore abort errors
        if (err instanceof Error && err.name !== "AbortError") {
          // Fallback to copy link
          copy(shareUrl);
        }
      }
    }
  }

  // Copy link to clipboard
  function handleCopyLink() {
    copy(shareUrl);
  }

  // Share on Twitter/X
  function handleTwitterShare() {
    const text = encodeURIComponent(`${title}\n${description}`);
    const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`;
    window.open(
      twitterUrl,
      "_blank",
      "noopener,noreferrer,width=550,height=420"
    );
    setIsOpen(false);
  }

  // Share on Facebook
  function handleFacebookShare() {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(
      facebookUrl,
      "_blank",
      "noopener,noreferrer,width=550,height=420"
    );
    setIsOpen(false);
  }

  // Share on LinkedIn
  function handleLinkedInShare() {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(
      linkedInUrl,
      "_blank",
      "noopener,noreferrer,width=550,height=420"
    );
    setIsOpen(false);
  }

  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenuTrigger asChild>
        <Button className={className} size={size} variant={variant}>
          <Share2 className="h-4 w-4" />
          {showLabel && <span className="ml-2">Share</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Native Share (only shown on supported devices) */}
        {typeof window !== "undefined" && (
          <>
            <DropdownMenuItem onClick={handleNativeShare}>
              <Share2 className="mr-2 h-4 w-4" />
              <span>Share via...</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Copy Link */}
        <DropdownMenuItem onClick={handleCopyLink}>
          {copied ? (
            <>
              <Check className="mr-2 h-4 w-4 text-green-500" />
              <span className="text-green-500">Link copied!</span>
            </>
          ) : (
            <>
              <Copy className="mr-2 h-4 w-4" />
              <span>Copy link</span>
            </>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Social Media Options */}
        <DropdownMenuItem onClick={handleTwitterShare}>
          <Twitter className="mr-2 h-4 w-4" />
          <span>Share on X (Twitter)</span>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleFacebookShare}>
          <Facebook className="mr-2 h-4 w-4" />
          <span>Share on Facebook</span>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={handleLinkedInShare}>
          <Linkedin className="mr-2 h-4 w-4" />
          <span>Share on LinkedIn</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
